// Per-branch loader for the FAQ questions.
//
// WHAT CHANGED AND WHY
// --------------------
// Until 2026-08-10 `faqData.js` imported all twelve dataset modules statically,
// so every visitor of /faq downloaded all 383 questions — including the club
// tiers — and a role filter in the UI decided what to *render*. Marin:
// "Hab direkt einen fehler gesehen, für andere rollen darfst du nicht anzeigen,
// der Agent und Agency darf sicher nicht wissen was alles die Clubs haben!"
// A UI filter is worthless against the network tab, so the split moved into the
// delivery: each module is its own webpack chunk, and only the chunks of the
// viewer's own branches are ever requested.
//
// The full dataset still exists in faqData.js for the offline consumers (the
// bundle-sync test and faq-pipeline/scripts/check-contract.js). Nothing under
// src/ may import it — faqDatasetIsolation.test.js fails the build if it does.
import { toQuestion, sortQuestions } from "./faqStructure";
import { modulesForBranches } from "./faqBranchModules";

// One entry per module of FAQ_MODULE_BRANCHES. Static `import()` calls (no
// computed paths) so webpack emits exactly these chunks and nothing pulls a
// foreign one in by accident.
const DATA_CHUNKS = {
  basis: () => import(/* webpackChunkName: "faq-data-basis" */ "./data/basis"),
  agent: () => import(/* webpackChunkName: "faq-data-agent" */ "./data/agent"),
  agentClub: () => import(/* webpackChunkName: "faq-data-agent-club" */ "./data/agentClub"),
  agentClubStaff: () =>
    import(/* webpackChunkName: "faq-data-agent-club-staff" */ "./data/agentClubStaff"),
  agentAgencyAdmin: () =>
    import(/* webpackChunkName: "faq-data-agent-agencyadmin" */ "./data/agentAgencyAdmin"),
  agencyMember: () =>
    import(/* webpackChunkName: "faq-data-agencymember" */ "./data/agencyMember"),
  agencyAdmin: () => import(/* webpackChunkName: "faq-data-agencyadmin" */ "./data/agencyAdmin"),
  staffFree: () => import(/* webpackChunkName: "faq-data-stafffree" */ "./data/staffFree"),
  staffAgent: () => import(/* webpackChunkName: "faq-data-staff-agent" */ "./data/staffAgent"),
  clubMember: () => import(/* webpackChunkName: "faq-data-clubmember" */ "./data/clubMember"),
  clubAdmin: () => import(/* webpackChunkName: "faq-data-clubadmin" */ "./data/clubAdmin"),
  lawyer: () => import(/* webpackChunkName: "faq-data-lawyer" */ "./data/lawyer"),
  player: () => import(/* webpackChunkName: "faq-data-player" */ "./data/player"),
  organizer: () =>
    import(/* webpackChunkName: "faq-data-organizer" */ "./data/organizer"),
};

// Module name → resolved raw entries. Keeps a second visit (or a dev role
// switch) off the network and makes the promise shareable between callers.
const cache = new Map();

const loadModule = (name) => {
  if (!cache.has(name)) {
    cache.set(
      name,
      DATA_CHUNKS[name]().then((mod) => mod.default)
    );
  }
  return cache.get(name);
};

/**
 * The questions this viewer is allowed to have, in page order.
 *
 * @param {Set<string>|Array<string>} branches the viewer's own audience branches
 * @returns {Promise<Array>} page-shaped questions — never a foreign row
 */
export const loadFaqQuestions = async (branches) => {
  const names = modulesForBranches(branches);
  const loaded = await Promise.all(names.map(loadModule));
  // Modules are branch-scoped, not viewer-scoped: a viewer owning both 1a and
  // 1c loads agent AND agentClub, which is correct — but a row must still be
  // rendered once, so ids are de-duplicated across modules.
  const seen = new Set();
  const questions = [];
  loaded.forEach((entries) => {
    entries.forEach((entry) => {
      if (seen.has(entry.id)) return;
      seen.add(entry.id);
      questions.push(toQuestion(entry));
    });
  });
  return sortQuestions(questions);
};

// Test hook — lets Jest start from a cold cache.
export const __resetFaqDataCache = () => cache.clear();

export default loadFaqQuestions;
